import { NfcVerdict } from "services/pcgPublic";

/*
  Pure presentation layer for an NFC verification verdict (REQ-041, REQ-042, REQ-047).
  No component, no fetch — VerdictBand (src/components/VerdictBand.tsx) is the only
  consumer today; P2's mutation and P4's report wiring read `isNfcVerdict` /
  `readVerdict` to gate whether the band renders at all (R9/D14: a report reached
  without a tap must show nothing).
*/

export type VerdictTone = "success" | "info" | "danger";

export interface VerdictPresentation {
  tone: VerdictTone;
  label: string;
  detail: string;
  icon: "check" | "info" | "alert";
}

// REQ-047 — exact wording, do not paraphrase.
export const HONESTY_LINE =
  "Tapping the chip proves the chip is one of ours and has not been copied.";

/*
  Detail strings follow spec D12's "Customer is told" column
  (docs/specs/nfc-slab-authentication.md, ~line 739).

  UNKNOWN_CHIP's detail must contain the literal text "not a PCG chip" — P2's test
  asserts on that substring.

  REPLAYED's copy is a neutral placeholder awaiting Robert's approval (§13 item 5,
  global-constraints.md). Verbatim from that file — it must not accuse the customer
  of anything ("already used" reads as fact, not fault).
*/
const PRESENTATIONS: Record<NfcVerdict, VerdictPresentation> = {
  VERIFIED: {
    tone: "success",
    label: "Verified genuine",
    detail: "This chip has been verified as genuine.",
    icon: "check",
  },
  REPLAYED: {
    tone: "info",
    label: "Link already used",
    detail:
      "This link was already used. Tap the chip on the slab to verify it yourself.",
    icon: "info",
  },
  SUPERSEDED: {
    tone: "info",
    label: "Chip replaced",
    detail: "This chip has been replaced.",
    icon: "info",
  },
  KEY_RETIRED: {
    tone: "info",
    label: "Verification withdrawn",
    detail: "Verification for this chip has been withdrawn. Contact PCG.",
    icon: "info",
  },
  INVALID: {
    tone: "danger",
    label: "Could not verify",
    detail: "This chip could not be verified.",
    icon: "alert",
  },
  UNKNOWN_CHIP: {
    tone: "danger",
    label: "Not a PCG chip",
    detail: "This is not a PCG chip.",
    icon: "alert",
  },
};

export const presentVerdict = (v: NfcVerdict): VerdictPresentation =>
  PRESENTATIONS[v];

const NFC_VERDICTS: readonly NfcVerdict[] = [
  "UNKNOWN_CHIP",
  "INVALID",
  "KEY_RETIRED",
  "SUPERSEDED",
  "REPLAYED",
  "VERIFIED",
];

// Type guard, used by P4 to decide whether router state carries a real verdict.
export const isNfcVerdict = (v: unknown): v is NfcVerdict =>
  typeof v === "string" && (NFC_VERDICTS as readonly string[]).includes(v);

/*
  Reads { nfcVerdict } off router state (P4). Anything else — a non-object, a bare
  string, a missing key, or an unrecognised verdict string — is null. This is the
  only gate P4 has for R9/D14, so it has to fail closed.
*/
export const readVerdict = (state: unknown): NfcVerdict | null => {
  if (typeof state !== "object" || state === null) {
    return null;
  }
  const { nfcVerdict } = state as { nfcVerdict?: unknown };
  return isNfcVerdict(nfcVerdict) ? nfcVerdict : null;
};
